import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import { Slider } from '@/components/ui/slider';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@clerk/clerk-react';
import { Badge } from '@/components/ui/badge';
import { Check, ChevronsUpDown, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
} from "@/components/ui/command";
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "@/components/ui/popover";
import { AppHeader } from '@/components/layout/AppHeader';

type District = {
    id: string;
    name: string;
    state: string;
    city: string;
};

export function PrivacySetup() {
    const navigate = useNavigate();
    const { toast } = useToast();
    const { getToken, isLoaded, isSignedIn } = useAuth();

    const [districtBlockingEnabled, setDistrictBlockingEnabled] = useState(false);
    const [selectedDistricts, setSelectedDistricts] = useState<District[]>([]);
    const [blockedZips, setBlockedZips] = useState<string[]>([]);
    const [zipInput, setZipInput] = useState('');
    const [radiusEnabled, setRadiusEnabled] = useState(false);
    const [radiusMiles, setRadiusMiles] = useState(15);
    const [schoolHoursEnabled, setSchoolHoursEnabled] = useState(false);
    const [startTime, setStartTime] = useState('07:00');
    const [endTime, setEndTime] = useState('16:00');
    const [loading, setLoading] = useState(false);
    const [initialLoading, setInitialLoading] = useState(true);

    // District search state
    const [openDistrictSearch, setOpenDistrictSearch] = useState(false);
    const [districtQuery, setDistrictQuery] = useState('');
    const [foundDistricts, setFoundDistricts] = useState<District[]>([]);
    const [searchingDistricts, setSearchingDistricts] = useState(false);

    // Fetch existing settings
    useEffect(() => {
        // Wait for Clerk to load before trying to get token
        if (!isLoaded) {
            console.log('[PrivacySetup] Waiting for Clerk to load...');
            return;
        }

        if (!isSignedIn) {
            console.log('[PrivacySetup] User not signed in');
            setInitialLoading(false);
            return;
        }

        const fetchSettings = async () => {
            try {
                const token = await getToken();
                if (!token) {
                    console.log('[PrivacySetup] No auth token available yet, skipping settings fetch');
                    setInitialLoading(false);
                    return;
                }

                const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:8787';
                const response = await fetch(`${apiUrl}/api/privacy/settings`, {
                    headers: { Authorization: `Bearer ${token}` },
                });

                // For new users during onboarding, settings won't exist yet (404)
                if (response.status === 404) {
                    console.log('[PrivacySetup] No existing settings found (new user)');
                    setInitialLoading(false);
                    return;
                }

                if (response.ok) {
                    const data = await response.json();
                    const s = data.settings;

                    setDistrictBlockingEnabled(s.blockedDistricts.length > 0);
                    // Note: We only get IDs back, ideally we'd fetch full district objects.
                    // For now, we might need another endpoint to get district details by IDs or just store IDs.
                    // Since the UI needs names, we should probably fetch them.
                    // Skipping full hydration for now to keep it simple, assuming empty start or handled later.
                    // Or we can just store IDs if we don't display names for existing ones (bad UX).
                    // Let's assume for MVP we start fresh or just show IDs if names missing.

                    setBlockedZips(s.blockedZipCodes || []);
                    setRadiusEnabled(!!s.blockRadiusMiles);
                    setRadiusMiles(s.blockRadiusMiles || 15);
                    setSchoolHoursEnabled(s.schoolHoursInvisible || false);
                    setStartTime(s.schoolHoursStart || '07:00');
                    setEndTime(s.schoolHoursEnd || '16:00');
                } else {
                    console.error('[PrivacySetup] Failed to fetch settings:', response.status, response.statusText);
                }
            } catch (error) {
                console.error('[PrivacySetup] Error fetching settings:', error);
            } finally {
                setInitialLoading(false);
            }
        };

        fetchSettings();
    }, [getToken, isLoaded, isSignedIn]);

    // Search districts
    useEffect(() => {
        if (districtQuery.length < 2) {
            setFoundDistricts([]);
            return;
        }

        const searchDistricts = async () => {
            setSearchingDistricts(true);
            try {
                const token = await getToken();
                const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:8787';
                const response = await fetch(`${apiUrl}/api/privacy/districts?q=${encodeURIComponent(districtQuery)}`, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                if (response.ok) {
                    const data = await response.json();
                    setFoundDistricts(data.districts);
                }
            } catch (error) {
                console.error('Search failed:', error);
            } finally {
                setSearchingDistricts(false);
            }
        };

        const debounce = setTimeout(searchDistricts, 300);
        return () => clearTimeout(debounce);
    }, [districtQuery, getToken]);

    const handleAddZip = () => {
        if (!/^\d{5}$/.test(zipInput)) {
            toast({
                title: 'Invalid ZIP Code',
                description: 'Please enter a valid 5-digit ZIP code.',
                variant: 'destructive',
            });
            return;
        }
        if (blockedZips.includes(zipInput)) {
            setZipInput('');
            return;
        }
        if (blockedZips.length >= 20) {
            toast({
                title: 'Limit Reached',
                description: 'You can block up to 20 ZIP codes.',
                variant: 'destructive',
            });
            return;
        }
        setBlockedZips([...blockedZips, zipInput]);
        setZipInput('');
    };

    const removeZip = (zip: string) => {
        setBlockedZips(blockedZips.filter(z => z !== zip));
    };

    const toggleDistrict = (district: District) => {
        if (selectedDistricts.find(d => d.id === district.id)) {
            setSelectedDistricts(selectedDistricts.filter(d => d.id !== district.id));
        } else {
            setSelectedDistricts([...selectedDistricts, district]);
        }
        setOpenDistrictSearch(false);
    };

    const removeDistrict = (id: string) => {
        setSelectedDistricts(selectedDistricts.filter(d => d.id !== id));
    };

    const handleSave = async () => {
        setLoading(true);
        try {
            console.log('[PrivacySetup] handleSave called');
            const token = await getToken();
            console.log('[PrivacySetup] Got token:', token ? 'yes' : 'no');
            // Check if we should POST (create) or PATCH (update).
            // For simplicity, we'll try POST first, if 409 then PATCH.
            // Or better, just try POST and handle 409.

            const payload = {
                blockedDistricts: districtBlockingEnabled ? selectedDistricts.map(d => d.id) : [],
                blockedZipCodes: blockedZips,
                blockRadiusMiles: radiusEnabled ? radiusMiles : null,
                // blockRadiusLocation: ... (would need geolocation logic here)
                schoolHoursInvisible: schoolHoursEnabled,
                schoolHoursStart: startTime,
                schoolHoursEnd: endTime,
            };

            console.log('[PrivacySetup] Payload:', JSON.stringify(payload, null, 2));

            const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:8787';
            console.log('[PrivacySetup] API URL:', apiUrl);
            console.log('[PrivacySetup] Making POST request...');
            let response = await fetch(`${apiUrl}/api/privacy/settings`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify(payload),
            });

            console.log('[PrivacySetup] POST response status:', response.status);

            if (response.status === 409) {
                console.log('[PrivacySetup] Settings exist, trying PATCH...');
                // Already exists, update instead
                response = await fetch(`${apiUrl}/api/privacy/settings`, {
                    method: 'PATCH',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${token}`,
                    },
                    body: JSON.stringify(payload),
                });
                console.log('[PrivacySetup] PATCH response status:', response.status);
            }

            if (!response.ok) {
                const errorText = await response.text();
                console.error('[PrivacySetup] Error response:', errorText);
                throw new Error('Failed to save settings');
            }

            const result = await response.json();
            console.log('[PrivacySetup] Success! Result:', result);

            toast({
                title: 'Settings Saved',
                description: 'Your privacy settings have been updated.',
            });
            navigate('/profile/bio');
        } catch (error) {
            toast({
                title: 'Error',
                description: 'Failed to save privacy settings. Please try again.',
                variant: 'destructive',
            });
        } finally {
            setLoading(false);
        }
    };

    if (initialLoading || !isLoaded) {
        return <div className="flex justify-center items-center h-screen">Loading...</div>;
    }

    return (
        <>
            <AppHeader />
            <div className="container mx-auto max-w-4xl p-6 pb-24 pt-16">
                <h1 className="text-3xl font-bold mb-2 text-white">Privacy & Safety Settings</h1>
                <p className="text-muted-foreground mb-8">
                    Configure your privacy settings to control who can see your profile.
                </p>

            {/* District Blocking */}
            <Card className="mb-6 bg-card border-border">
                <CardHeader>
                    <CardTitle className="text-white">Block My School District</CardTitle>
                    <CardDescription>
                        Prevent users in your school district from seeing your profile
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    <div className="flex items-center space-x-2 mb-4">
                        <Switch
                            checked={districtBlockingEnabled}
                            onCheckedChange={setDistrictBlockingEnabled}
                        />
                        <label className="text-white">Enable district blocking</label>
                    </div>

                    {districtBlockingEnabled && (
                        <div className="space-y-4">
                            <Popover open={openDistrictSearch} onOpenChange={setOpenDistrictSearch}>
                                <PopoverTrigger asChild>
                                    <Button
                                        variant="outline"
                                        role="combobox"
                                        aria-expanded={openDistrictSearch}
                                        className="w-full justify-between text-white border-input bg-background hover:bg-accent hover:text-accent-foreground"
                                    >
                                        Select districts...
                                        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                                    </Button>
                                </PopoverTrigger>
                                <PopoverContent className="w-[400px] p-0">
                                    <Command shouldFilter={false}>
                                        <CommandInput
                                            placeholder="Search district..."
                                            value={districtQuery}
                                            onValueChange={setDistrictQuery}
                                        />
                                        <CommandList>
                                            {searchingDistricts && <div className="p-2 text-sm text-muted-foreground">Searching...</div>}
                                            {!searchingDistricts && foundDistricts.length === 0 && districtQuery.length >= 2 && (
                                                <CommandEmpty>No district found.</CommandEmpty>
                                            )}
                                            <CommandGroup>
                                                {foundDistricts.map((district) => (
                                                    <CommandItem
                                                        key={district.id}
                                                        value={district.id}
                                                        onSelect={() => toggleDistrict(district)}
                                                    >
                                                        <Check
                                                            className={cn(
                                                                "mr-2 h-4 w-4",
                                                                selectedDistricts.find(d => d.id === district.id) ? "opacity-100" : "opacity-0"
                                                            )}
                                                        />
                                                        {district.name} ({district.city}, {district.state})
                                                    </CommandItem>
                                                ))}
                                            </CommandGroup>
                                        </CommandList>
                                    </Command>
                                </PopoverContent>
                            </Popover>

                            <div className="flex flex-wrap gap-2">
                                {selectedDistricts.map((district) => (
                                    <Badge key={district.id} variant="secondary" className="pl-2 pr-1 py-1 flex items-center gap-1">
                                        {district.name}
                                        <button onClick={() => removeDistrict(district.id)} className="hover:bg-muted rounded-full p-0.5">
                                            <X className="h-3 w-3" />
                                        </button>
                                    </Badge>
                                ))}
                            </div>
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* ZIP Code Blocking */}
            <Card className="mb-6 bg-card border-border">
                <CardHeader>
                    <CardTitle className="text-white">Block Specific ZIP Codes</CardTitle>
                    <CardDescription>
                        Add ZIP codes where you don't want to be visible
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    <div className="flex gap-2 mb-4">
                        <Input
                            placeholder="Enter 5-digit ZIP"
                            value={zipInput}
                            onChange={(e) => setZipInput(e.target.value)}
                            maxLength={5}
                            className="max-w-[200px] bg-background text-white border-input"
                        />
                        <Button onClick={handleAddZip} variant="secondary">Add</Button>
                    </div>
                    <div className="flex flex-wrap gap-2">
                        {blockedZips.map((zip) => (
                            <Badge key={zip} variant="secondary" className="pl-2 pr-1 py-1 flex items-center gap-1">
                                {zip}
                                <button onClick={() => removeZip(zip)} className="hover:bg-muted rounded-full p-0.5">
                                    <X className="h-3 w-3" />
                                </button>
                            </Badge>
                        ))}
                    </div>
                </CardContent>
            </Card>

            {/* Radius Blocking */}
            <Card className="mb-6 bg-card border-border">
                <CardHeader>
                    <CardTitle className="text-white">Block Radius Around Location</CardTitle>
                    <CardDescription>
                        Hide your profile from users within a radius of your location
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    <div className="flex items-center space-x-2 mb-6">
                        <Switch checked={radiusEnabled} onCheckedChange={setRadiusEnabled} />
                        <label className="text-white">Enable radius blocking</label>
                    </div>
                    {radiusEnabled && (
                        <div className="space-y-6">
                            <div className="px-2">
                                <Slider
                                    value={[radiusMiles]}
                                    onValueChange={([value]) => setRadiusMiles(value)}
                                    min={5}
                                    max={50}
                                    step={5}
                                    className="py-4"
                                />
                            </div>
                            <p className="text-sm text-muted-foreground">
                                Blocking within <span className="font-bold text-primary">{radiusMiles} miles</span> of your current location.
                                (Location permission required)
                            </p>
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* School Hours */}
            <Card className="mb-6 bg-card border-border">
                <CardHeader>
                    <CardTitle className="text-white">School-Hour Invisibility</CardTitle>
                    <CardDescription>
                        Automatically hide your profile during school hours
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    <div className="flex items-center space-x-2 mb-6">
                        <Switch checked={schoolHoursEnabled} onCheckedChange={setSchoolHoursEnabled} />
                        <label className="text-white">Enable school-hour invisibility</label>
                    </div>
                    {schoolHoursEnabled && (
                        <div className="flex gap-4">
                            <div className="space-y-2">
                                <label className="text-sm text-muted-foreground">Start Time</label>
                                <Input
                                    type="time"
                                    value={startTime}
                                    onChange={(e) => setStartTime(e.target.value)}
                                    className="bg-background text-white border-input"
                                />
                            </div>
                            <div className="space-y-2">
                                <label className="text-sm text-muted-foreground">End Time</label>
                                <Input
                                    type="time"
                                    value={endTime}
                                    onChange={(e) => setEndTime(e.target.value)}
                                    className="bg-background text-white border-input"
                                />
                            </div>
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Actions */}
            <div className="flex justify-between items-center mt-8">
                <Button
                    variant="ghost"
                    onClick={() => navigate('/profile/photos')}
                    className="text-muted-foreground hover:text-white"
                >
                    Skip for Now
                </Button>
                <Button
                    onClick={handleSave}
                    disabled={loading}
                    className="bg-brand-primary hover:bg-brand-primary/90 text-white min-w-[150px]"
                >
                    {loading ? 'Saving...' : 'Save & Continue'}
                </Button>
            </div>
            </div>
        </>
    );
}
